import { useState, useCallback, DragEvent } from 'react';

export type DragReorderState<T> = {
  isDragging: boolean;
  dragIndex: number | null;
  dropIndex: number | null;
  draggedItem: T | null;
};

export type DragReorderHandlers = {
  handleDragStart: (index: number) => (e: DragEvent<HTMLElement>) => void;
  handleDragEnd: () => void;
  handleDragOver: (index: number) => (e: DragEvent<HTMLElement>) => void;
  handleDragLeave: () => void;
  handleDrop: (index: number) => (e: DragEvent<HTMLElement>) => void;
};

export type UseDragReorderResult<T> = {
  dragState: DragReorderState<T>;
  handlers: DragReorderHandlers;
};

export function useDragReorder<T>(
  items: T[],
  onReorder?: (reorderedItems: T[]) => void
): UseDragReorderResult<T> {
  const [dragState, setDragState] = useState<DragReorderState<T>>({
    isDragging: false,
    dragIndex: null,
    dropIndex: null,
    draggedItem: null
  });

  const handleDragStart = useCallback(
    (index: number) => (e: DragEvent<HTMLElement>) => {
      e.stopPropagation();
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(index));

      // setTimeout fixes a bug where handleDragEnd is called immediately on the last index item
      setTimeout(() => {
        setDragState({
          isDragging: true,
          dragIndex: index,
          dropIndex: null,
          draggedItem: items[index]
        });
      }, 0);
    },
    [items]
  );

  const handleDragEnd = useCallback(() => {
    setDragState({
      isDragging: false,
      dragIndex: null,
      dropIndex: null,
      draggedItem: null
    });
  }, []);

  const handleDragOver = useCallback(
    (index: number) => (e: DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';

      setDragState((prev) => ({
        ...prev,
        dropIndex: index
      }));
    },
    []
  );

  const handleDragLeave = useCallback(() => {
    setDragState((prev) => ({
      ...prev,
      dropIndex: null
    }));
  }, []);

  const handleDrop = useCallback(
    (dropIndex: number) => (e: DragEvent<HTMLElement>) => {
      e.preventDefault();

      const dragIndex = dragState.dragIndex;
      if (dragIndex === null || dragIndex === dropIndex) {
        handleDragEnd();
        return;
      }

      const reorderedItems = reorderArray(items, dragIndex, dropIndex);
      if (onReorder) {
        onReorder(reorderedItems);
      }

      handleDragEnd();
    },
    [dragState.dragIndex, items, onReorder, handleDragEnd]
  );

  return {
    dragState,
    handlers: {
      handleDragStart,
      handleDragEnd,
      handleDragOver,
      handleDragLeave,
      handleDrop
    }
  };
}

function reorderArray<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  const result = [...items];
  const [removed] = result.splice(fromIndex, 1);
  // When moving forward, removal shifts indices left, so the original target is now one earlier.
  const targetIndex = toIndex > fromIndex ? toIndex - 1 : toIndex;
  result.splice(targetIndex, 0, removed);
  return result;
}
